/*
 * product-extras.js — Abstracta Decor product page
 *
 * Three things live here, all hanging off one idea: the minutes around the
 * buy button are the only ones that decide anything, so that is where the
 * useful information goes.
 *
 *   1. The info strip's free-delivery line, which counts down the rupees
 *      still needed rather than only stating a threshold.
 *   2. A sticky bar on mobile, because the real button scrolls away while
 *      the visitor reads and a lot of them never scroll back.
 *   3. A toast after add-to-cart carrying the same free-delivery number.
 *      That moment — item just added, cart open in the mind — is when a
 *      second item actually gets added.
 *
 * Copy is in English throughout. An earlier pass had these strings in
 * Roman Urdu; the rest of the storefront is English, and one component
 * switching language mid-page reads as an oversight rather than as warmth.
 *
 * Cart state is read once and then kept in sync by riding along with the
 * cart fetches the theme already makes, rather than polling.
 */

(function () {
  var THRESHOLD = 499900; // Rs 4,999 in paisa — must match the shipping rate
  var listeners = [];
  var cartTotal = null;

  function money(paisa) {
    return 'Rs.' + Math.round(paisa / 100).toLocaleString('en-PK');
  }

  function remaining() {
    if (cartTotal === null) return null;
    return Math.max(0, THRESHOLD - cartTotal);
  }

  function broadcast() {
    listeners.forEach(function (fn) {
      try { fn(remaining(), cartTotal); } catch (e) {}
    });
  }

  function refresh() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(root + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (c) { cartTotal = c.total_price; broadcast(); return c; })
      .catch(function () {});
  }

  window.AbstractaCart = {
    onChange: function (fn) { listeners.push(fn); if (cartTotal !== null) fn(remaining(), cartTotal); },
    refresh: refresh,
    remaining: remaining,
    money: money,
    threshold: THRESHOLD
  };

  var origFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var href = typeof url === 'string' ? url : (url && url.url) || '';
    var isCartWrite = /\/cart\/(add|change|update|clear)/.test(href);
    return origFetch.apply(this, arguments).then(function (res) {
      if (isCartWrite && res.ok) {
        refresh();
        if (/\/cart\/add/.test(href)) showToast();
      }
      return res;
    });
  };

  /* ------------------------------------------------------------- Toast */

  var toastEl = null;
  var toastTimer = null;

  function buildToast() {
    var el = document.createElement('div');
    el.className = 'pt-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML =
      '<span class="pt-toast__tick">✓</span>' +
      '<span class="pt-toast__copy">' +
        '<b class="pt-toast__title">Added to cart</b>' +
        '<span class="pt-toast__sub"></span>' +
      '</span>' +
      '<a class="pt-toast__cta" href="/cart">View cart</a>';
    document.body.appendChild(el);
    return el;
  }

  function showToast() {
    if (!toastEl) toastEl = buildToast();
    var sub = toastEl.querySelector('.pt-toast__sub');

    // The new cart total lands a moment after the add resolves, so the line
    // is written on the next refresh rather than guessed at now.
    refresh().then(function () {
      var left = remaining();
      if (left === null) { sub.textContent = ''; return; }
      sub.textContent = left > 0
        ? money(left) + ' more for free delivery'
        : 'Your delivery is free';
    });

    toastEl.classList.remove('is-in');
    void toastEl.offsetWidth;   // restart the entry animation
    toastEl.classList.add('is-in');

    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-in'); }, 4200);
  }

  /* ------------------------------------------------------- Info strip */

  if (!customElements.get('product-info-strip')) {
    customElements.define('product-info-strip', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.pinfo__track');
        if (!this.track) return;

        this.setDuration = this.setDuration.bind(this);
        this.hold = () => this.classList.add('is-held');
        this.release = () => this.classList.remove('is-held');

        this.pxPerSecond = 42;
        this.setDuration();

        this.addEventListener('touchstart', this.hold, { passive: true });
        this.addEventListener('touchend', this.release, { passive: true });
        this.addEventListener('touchcancel', this.release, { passive: true });

        if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.setDuration);
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(this.setDuration);
          this.observer.observe(this.track);
        }

        // Both copies have to update — the track is printed twice so the
        // loop can restart without a visible jump.
        var slots = this.querySelectorAll('[data-free-delivery]');
        var self = this;
        window.AbstractaCart.onChange(function (left, total) {
          var html;
          if (total === 0 || total === null) {
            // An empty cart has nothing to count down from. "Rs.4,999 more"
            // on a cart with nothing in it reads as a price, not a target.
            html = 'Free delivery above <b>Rs.4,999</b>';
          } else if (left > 0) {
            html = '<b>' + money(left) + '</b> more for free delivery';
          } else {
            html = 'Your delivery is <b>free</b>';
          }
          slots.forEach(function (s) { s.innerHTML = html; });
          self.setDuration();
        });
      }

      disconnectedCallback() { if (this.observer) this.observer.disconnect(); }

      setDuration() {
        var d = this.track.scrollWidth / 2;
        if (!d) return;
        this.style.setProperty('--pinfo-duration', Math.max(14, d / this.pxPerSecond).toFixed(2) + 's');
      }
    });
  }

  /* ------------------------------------------------------ Sticky bar */

  function initSticky() {
    var bar = document.querySelector('.psticky');
    var realBtn = document.querySelector('product-form button[name="add"], .product-form__submit');
    if (!bar || !realBtn) return;

    var proxy = bar.querySelector('.psticky__btn');
    proxy.addEventListener('click', function () { realBtn.click(); });

    function sync() {
      proxy.disabled = realBtn.disabled;
      proxy.textContent = realBtn.disabled ? 'Sold out' : 'Add';
    }
    sync();
    new MutationObserver(sync).observe(realBtn, { attributes: true, attributeFilter: ['disabled'] });

    // Only worth showing once the real button has left the screen.
    new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { threshold: 0 }).observe(realBtn);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initSticky(); refresh(); });
  } else {
    initSticky();
    refresh();
  }
})();
